/**
 * @fileoverview Minimal plug-and-play tabs custom element
 *
 * @example
 * // Basic usage
 * <tabs-element default="overview">
 *   <tabs-list>
 *     <tabs-trigger value="overview">Overview</tabs-trigger>
 *     <tabs-trigger value="details">Details</tabs-trigger>
 *   </tabs-list>
 *   <tabs-content value="overview">
 *     <p>Overview content</p>
 *   </tabs-content>
 *   <tabs-content value="details">
 *     <p>Details content</p>
 *   </tabs-content>
 * </tabs-element>
 *
 * @example
 * // Programmatic activation via window event
 * window.dispatchEvent(new CustomEvent('activate-tabs-my-tabs', { detail: { value: 'details' } }));
 */

// Inject functional styles once — visual styling is left to the implementer
if (!document.getElementById('component-tabs-styles')) {
  const style = document.createElement('style');
  style.id = 'component-tabs-styles';
  style.textContent = `
    tabs-content { display: none; }
    tabs-content.is-active { display: block; }
    tabs-trigger { cursor: pointer; }

    @media (prefers-reduced-motion: no-preference) {
      tabs-content.is-active {
        animation: tabs-fade-in var(--tabs-transition-duration, 0.2s) ease;
      }
    }

    @keyframes tabs-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

class TabsElement extends HTMLElement {
  #activeValue = null;
  #boundActivate = null;

  /**
   * Finds this element's own triggers/panels. Tries the direct-child form
   * first, then falls back to a descendant search for theme-block-driven tabs,
   * where each block emits its own trigger and panel and may be wrapped.
   * Filters to nodes this tabs-element owns so nested tab sets stay separate.
   *
   * @param {string} strict - direct-child selector
   * @param {string} loose - descendant selector
   * @returns {Element[]}
   */
  #own(strict, loose) {
    const direct = this.querySelectorAll(strict);
    if (direct.length) return [...direct];
    return [...this.querySelectorAll(loose)].filter(
      (el) => el.closest('tabs-element') === this
    );
  }

  /** @returns {Element[]} */
  get #triggers() {
    return this.#own(':scope > tabs-list > tabs-trigger', 'tabs-trigger');
  }

  /** @returns {Element[]} */
  get #panels() {
    return this.#own(':scope > tabs-content', 'tabs-content');
  }

  /**
   * APG tabs contract: each tab points at its panel (aria-controls), each
   * panel back at its tab (aria-labelledby), and panels are focusable so a
   * keyboard user can scroll their content.
   */
  #wireAria() {
    const uid = this.id || Math.random().toString(36).slice(2);
    const panels = this.#panels;
    this.#triggers.forEach((trigger) => {
      const value = trigger.getAttribute('value');
      if (!value) return;
      if (!trigger.id) trigger.id = `tab-${uid}-${value}`;
      const panel = panels.find((p) => p.getAttribute('value') === value);
      if (!panel) return;
      if (!panel.id) panel.id = `tabpanel-${uid}-${value}`;
      trigger.setAttribute('aria-controls', panel.id);
      panel.setAttribute('aria-labelledby', trigger.id);
      if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '0');
    });
  }

  connectedCallback() {
    this.#boundActivate = (e) => this.activate(e.detail?.value);

    // Window event for external control
    const id = this.id;
    if (id) {
      window.addEventListener(`activate-tabs-${id}`, this.#boundActivate);
    }

    this.#wireAria();

    // Activate default tab, fall back to first trigger
    const defaultValue =
      this.getAttribute('default') ?? this.#triggers[0]?.getAttribute('value');

    if (defaultValue) {
      this.activate(defaultValue, false);
    }
  }

  disconnectedCallback() {
    const id = this.id;
    if (id) {
      window.removeEventListener(`activate-tabs-${id}`, this.#boundActivate);
    }
  }

  /**
   * Activates a tab by value.
   * @param {string} value - The value matching a tabs-trigger and tabs-content
   * @param {boolean} dispatch - Whether to dispatch tabs:change event (default true)
   */
  activate(value, dispatch = true) {
    if (!value) return;
    // Always sync the DOM, even for a no-op value: connectedCallback's initial
    // activate must engage the roving tabindex (every trigger starts at
    // tabindex="0"), and an early return here used to skip that when the
    // initial value already matched.
    const changed = value !== this.#activeValue;
    this.#activeValue = value;

    this.#triggers.forEach((trigger) => {
      const isActive = trigger.getAttribute('value') === value;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      trigger.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.#panels.forEach((content) => {
      content.classList.toggle('is-active', content.getAttribute('value') === value);
    });

    if (dispatch && changed) {
      this.dispatchEvent(new CustomEvent('tabs:change', { bubbles: true, detail: { value } }));
    }
  }

  /** @returns {string|null} The currently active tab value */
  get activeValue() {
    return this.#activeValue;
  }
}

class TabsList extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'tablist');
  }
}

class TabsTrigger extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'tab');
    this.setAttribute('tabindex', '0');

    this.addEventListener('click', () => {
      this.closest('tabs-element')?.activate(this.getAttribute('value'));
    });

    // Arrow key navigation between triggers
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
        return;
      }

      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;

      e.preventDefault();
      // Theme-block-driven tabs have no tabs-list to scope to
      const scope = this.closest('tabs-list') ?? this.closest('tabs-element');
      const triggers = [...(scope?.querySelectorAll('tabs-trigger') ?? [])].filter(
        (el) => el.closest('tabs-element') === this.closest('tabs-element')
      );
      const current = triggers.indexOf(this);
      const next =
        e.key === 'ArrowRight'
          ? (current + 1) % triggers.length
          : (current - 1 + triggers.length) % triggers.length;

      triggers[next]?.click();
      triggers[next]?.focus();
    });
  }
}

class TabsContent extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'tabpanel');
  }
}

// Guarded: this file is loaded from more than one section (and in mixed
// classic/module script modes), and a second evaluation must not throw
// NotSupportedError on an already-registered name.
if (!customElements.get('tabs-element')) customElements.define('tabs-element', TabsElement);
if (!customElements.get('tabs-list')) customElements.define('tabs-list', TabsList);
if (!customElements.get('tabs-trigger')) customElements.define('tabs-trigger', TabsTrigger);
if (!customElements.get('tabs-content')) customElements.define('tabs-content', TabsContent);
